import { EyeIcon, MessageCircleIcon, UsersIcon } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Marker, MarkerContent, MarkerIcon } from "@/components/ui/marker"
import { MessageGroup } from "@/components/ui/message"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useAuth, useCan } from "@/features/auth/auth-context"
import {
  ConversationRuns,
  ConversationSkeleton,
  groupMessages,
} from "@/features/messages/conversation-parts"
import { MessageComposer } from "@/features/messages/MessageComposer"
import { useConversationEnd } from "@/features/messages/use-conversation-end"
import {
  conversationCounts,
  flattenMessages,
  isModuleOff,
  useMarkConversationRead,
  useMessages,
} from "@/features/messages/use-messages"
import { ApiError } from "@/lib/api-error"

/**
 * THE SHARED ROOM — §2's three people, everything said in front of everyone.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS A PANE NOW, NOT A SCREEN (2026-08-19), AND THAT IS THE ONLY THING THAT
 * CHANGED ABOUT IT.
 *
 * This was `MessagesScreen.tsx` and it WAS `/messages`. The owner asked for the
 * list to be the front door — his father, his accountant, and the group chat as
 * a row below them — so the room now opens at `/messages/group`, inside
 * `MessengerScreen`'s pane, beside the same list every private conversation
 * opens from. Same messages, same order, same capability, same flag, same
 * components. Nothing about the conversation itself moved.
 *
 * EVERY MESSAGE IS RENDERED AS TEXT. NEVER AS HTML, NEVER AS MARKDOWN.
 *
 * The body is stored exactly as typed — "cut the plate <100mm" survives the API
 * unchanged, deliberately (see `MessageBodySchema`) — which is only safe because
 * every surface renders it through `{message.body}`, which React escapes by
 * construction. There is no `dangerouslySetInnerHTML` in this feature and there
 * must never be one; `whitespace-pre-wrap` is what makes a two-line message read
 * as two lines, and it is a CSS property, not a parser.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * WHAT THIS IS NOT: the notification hub. That is an INBOX — rows addressed to
 * one person, each with its own read state, ordered unread-first. This is a
 * CONVERSATION — the same rows for everybody, in the order they were said, with
 * one watermark per person. They share the header corner and the chrome; they do
 * not share a table, a screen or a badge.
 *
 * ONE WATERMARK PER PERSON, WHICH IS WHAT STILL SEPARATES THIS FROM A PRIVATE
 * THREAD. `DirectThread` keeps one per PAIR. Both read models live behind the
 * same list now, and neither borrows the other's count — see `MessengerScreen`.
 *
 * NO PER-PERSON READ RECEIPTS ARE SHOWN, and that is a correctness decision
 * rather than a scope cut. The CA cannot sign in at all in v1
 * (`accountant_role`), so a tick reading "seen by everyone" would be a lie the
 * owner would act on — he would assume his accountant had read something she
 * cannot reach. The pane therefore says who SAID what and when, and claims
 * nothing about who has read it.
 */
export function GroupRoom(): React.JSX.Element {
  const { t } = useTranslation()
  const canSend = useCan("message.send")
  const { user } = useAuth()
  const query = useMessages()
  const markRead = useMarkConversationRead()

  const messages = flattenMessages(query.data)
  const { total } = conversationCounts(query.data)

  /*
   * "MINE" IS THE SESSION'S USER ID AGAINST THE AUTHOR'S — never a role (§2.1).
   * Nothing here or in `groupMessages` knows what an OWNER is; the grouping is
   * told which id is the reader's and compares numbers.
   */
  const groups = groupMessages(messages, user?.id ?? null)

  /*
   * MARK READ WHEN THE NEWEST MESSAGE IS ON SCREEN — and `newestOnScreen` is
   * what makes that sentence true rather than merely written down.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * THE WATERMARK MOVES ON WHAT WAS SEEN, NEVER ON WHAT WAS FETCHED.
   *
   * Firing this off the ARRIVAL of the data is the version of this code that
   * loses a message. Page one is the newest fifty; the box shows six or seven at
   * a time. Posting `{upToId: <newest>}` on load would clear a badge of twenty
   * the instant the pane opened, having painted only the oldest seven of them —
   * and "don't send that bill, the HSN is wrong", sitting at the bottom unread,
   * would never be flagged again. There is no per-message read state, no "mark
   * unread", and messages are never deleted, so a badge cleared wrongly is
   * cleared for ever.
   *
   * So the watermark waits for the end of the conversation to actually reach the
   * viewport (`useConversationEnd`), which on opening happens because the same
   * hook scrolls there. Once per id, not per render, which is what the ref
   * guards — `>=` rather than `===` so a smaller id from a "load older" page can
   * never be re-posted. Fire-and-forget: nothing waits for it (see the hook),
   * and a failure leaves the badge up, which is the safe direction.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const newestId = messages.length === 0 ? null : (messages[messages.length - 1]?.id ?? null)
  const { endRef, newestOnScreen } = useConversationEnd(newestId)
  const markedRef = React.useRef(0)
  const mark = markRead.mutate
  React.useEffect(() => {
    if (newestId === null || !newestOnScreen) return
    if (markedRef.current >= newestId) return
    markedRef.current = newestId
    mark(newestId)
  }, [newestId, newestOnScreen, mark])

  return (
    <Card className="flex min-w-0 flex-col overflow-hidden py-0" data-testid="group-room">
      {/*
        * THE PANE NAMES ITSELF, exactly as a private thread does. Two panes that
        * open in the same place must both say which conversation you are in, or
        * the only difference between "everyone can read this" and "only the two
        * of you can" is which row happens to be highlighted in the list.
        */}
      <header className="flex items-center gap-2.5 border-b p-3 md:p-4">
        <Avatar size="sm" aria-hidden="true">
          <AvatarFallback>
            <UsersIcon className="size-4" />
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <h2 className="truncate text-sm font-semibold" data-testid="group-room-name">
            {t("chat.groupRow")}
          </h2>
          <p className="text-muted-foreground truncate text-xs">{t("chat.groupRowHelp")}</p>
        </div>
      </header>

      {/*
        * A 404 IS NOT HANDLED HERE. It can only mean the `messages` flag is off,
        * and that is a fact about the whole messenger rather than about this
        * conversation — so `MessengerScreen` reads it once and renders
        * `FeatureOff` in place of the entire surface, list included. Answering it
        * again in this pane would put "not switched on" beside a list of people
        * that is itself not switched on.
        */}
      {query.isError && !isModuleOff(query.error) ? (
        <Alert variant="destructive" className="m-3 w-auto md:m-4">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <ScrollArea className="h-[60vh] min-h-64">
        {/*
          * `MessageGroup` (components/ui/message.tsx) IS THE LIST CONTAINER —
          * the same `flex flex-col` this pane used to spell out by hand. The
          * bubbles, the day rules and the read-only line are all primitives, so a
          * change to the chat's look happens in one place for every surface that
          * shows a conversation rather than in this file's class strings.
          */}
        <MessageGroup className="gap-3 p-3 md:p-4">
          {query.isPending ? <ConversationSkeleton /> : null}

          {/*
            * "LOAD OLDER" AT THE TOP, WHICH IS WHERE OLDER IS. Paging runs
            * backwards through the conversation: the first page is the newest,
            * and this walks into the past.
            */}
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage()
                }}
              >
                {t("chat.loadOlder")}
              </Button>
            </div>
          ) : null}

          {/*
            * "NOTHING HAS BEEN SAID YET" IS A CLAIM ABOUT THE CONVERSATION, SO
            * IT NEEDS THE CONVERSATION — `query.data !== undefined`, which is how
            * the notification hub gates its own empty state.
            *
            * `isPending` is false in the ERROR state too, and
            * `conversationCounts(undefined)` reports a total of zero, so gating
            * on those two put the red "Couldn't load" banner and "Nothing has
            * been said yet" on screen together: the app asserting the
            * conversation is empty when the truth is that it is unknown. A failed
            * load says only that it failed.
            */}
          {query.data !== undefined && total === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <MessageCircleIcon />
                </EmptyMedia>
                <EmptyTitle>{t("chat.emptyTitle")}</EmptyTitle>
                <EmptyDescription>{t("chat.emptyHelp")}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : null}

          {/*
            * ONE `Message` PER RUN OF CONSECUTIVE MESSAGES FROM ONE PERSON, one
            * `Bubble` per message inside it — see `groupMessages`. THE ORDER AND
            * THE SET OF MESSAGES ARE UNTOUCHED: grouping only decides how often
            * the avatar and the author's name are repeated.
            */}
          <ConversationRuns groups={groups} />

          {/*
            * THE END OF THE CONVERSATION, AS AN ELEMENT. It carries no text and
            * no height; it exists so the browser can answer two questions this
            * pane cannot answer for itself — where to scroll on opening, and
            * whether the newest message is genuinely on screen before the
            * watermark is allowed to move past it. See `useConversationEnd`.
            */}
          <div
            ref={endRef}
            data-testid="conversation-end"
            aria-hidden="true"
            className="h-px w-full shrink-0"
          />
        </MessageGroup>
      </ScrollArea>

      {/*
        * THE COMPOSE BOX IS ABSENT, NOT DISABLED, WITHOUT `message.send`. A
        * greyed-out box invites a hunt for the way to un-grey it, and for a
        * capability the role does not hold there is no such way. The sentence in
        * its place says what is true: you can read this, you cannot add to it.
        * (Nobody is in that position today — all three roles hold both
        * capabilities — which is exactly why it is worth having the branch before
        * somebody is.)
        *
        * A PRIVATE THREAD HAS NO SUCH BRANCH, and that is not an oversight there:
        * `message.direct` gates reading and writing together on the server,
        * deliberately, because the alternative produces somebody who receives
        * private messages he is forbidden to answer.
        */}
      {canSend ? (
        <MessageComposer />
      ) : (
        /*
          * A STATUS LINE ABOUT THE CONVERSATION IS A `Marker`, exactly like the
          * day rules above it — same muted type, same size, so the two things
          * that are "the app talking about the conversation rather than somebody
          * talking in it" look alike and neither can be mistaken for a message.
          */
        <Marker className="border-t p-4" data-testid="read-only-notice">
          <MarkerIcon>
            <EyeIcon />
          </MarkerIcon>
          <MarkerContent>{t("chat.readOnly")}</MarkerContent>
        </Marker>
      )}
    </Card>
  )
}
